import React, { useState } from 'react';
import { Text } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCopy } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { PromptBrowser } from '../../shared';
import { PromptProject } from '../../../services/projectService';
import { useNotification } from '../../../context/NotificationContext';
import { getApiUrl } from '../../../config/api';
import styles from './TabContainer.module.css';

interface TabContainerProps {
  selectedTrack: any;
  currentTime: number;
  onVolumeChange: (trackId: string, volume: number) => void;
  onMuteToggle: (trackId: string) => void;
  onDuplicate: (trackId: string) => void;
  onSplit: (trackId: string) => void;
  onDelete: (trackId: string) => void;
  onFadeChange: (trackId: string, property: 'fadeInDuration' | 'fadeOutDuration', value: number) => void;
  onSoundEffectAdd: (soundEffectUrl: string, filename: string, duration: number) => void;
  onOpenSoundEffectsModal: () => void;
  userId: string;
  projectId?: string; // Add projectId prop
  refreshTrigger: number;
}

const TabContainer: React.FC<TabContainerProps> = ({
  selectedTrack,
  currentTime,
  onVolumeChange,
  onMuteToggle,
  onDuplicate,
  onSplit,
  onDelete,
  onFadeChange,
  onSoundEffectAdd,
  onOpenSoundEffectsModal,
  userId,
  projectId, // Add projectId to destructuring
  refreshTrigger
}) => {
  const { t } = useTranslation('audioEditor');
  const [activeTab, setActiveTab] = useState<'audio' | 'prompts'>('audio');
  const [selectedPrompt, setSelectedPrompt] = useState<PromptProject | null>(null);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  
  const { showNotification } = useNotification();

  // Get display title - prefer prompt title over project title
  const getPromptDisplayTitle = (project: PromptProject): string => {
    // First, try to use the project title if it's meaningful
    if (project.title && project.title !== 'Untitled Project' && project.title.trim() !== '') {
      return project.title;
    }
    
    // If no meaningful project title, try to generate one from the user's idea
    if (project.promptData?.userSelections?.userIdea) {
      const userIdea = project.promptData.userSelections.userIdea;
      // Truncate long ideas to create a readable title (max 50 characters)
      if (userIdea.length > 50) {
        return userIdea.substring(0, 50).trim() + '...';
      }
      return userIdea;
    }
    
    // Last fallback to project title even if it's generic
    return project.title || 'Untitled Prompt';
  };

  // Handle prompt selection to show modal
  const handlePromptSelect = (prompt: string, project: PromptProject) => {
    setSelectedPrompt(project);
    setIsPromptModalOpen(true);
  };

  // Handle copying prompt to clipboard
  const handleCopyPrompt = async (prompt: string, project: PromptProject) => {
    try {
      await navigator.clipboard.writeText(prompt);
      showNotification({
        title: 'Copied!',
        message: 'Prompt copied to clipboard',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      showNotification({
        title: 'Copy failed',
        message: 'Could not copy to clipboard',
        type: 'error'
      });
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className={styles.tabContainer}>
      {/* Tab Navigation */}
      <div className={styles.tabNav}>
        <button
          className={`${styles.tabButton} ${activeTab === 'audio' ? styles.active : ''}`}
          onClick={() => setActiveTab('audio')}
        >
          <Text size="sm" fw={600}>{t('mixer.title').replace(' Mixer', '')}</Text>
        </button>
        {/* Sound Effects tab hidden - functionality moved to separate page */}
        <button
          className={`${styles.tabButton} ${activeTab === 'prompts' ? styles.active : ''}`}
          onClick={() => setActiveTab('prompts')}
        >
          <Text size="sm" fw={600}>Prompts</Text>
        </button>
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === 'audio' && selectedTrack && (
          <div className={styles.audioTab}>
            <AudioTabContent
              selectedTrack={selectedTrack}
              currentTime={currentTime}
              onVolumeChange={onVolumeChange}
              onMuteToggle={onMuteToggle}
              onDuplicate={onDuplicate}
              onSplit={onSplit}
              onDelete={onDelete}
              onFadeChange={onFadeChange}
            />
          </div>
        )}
        
        {activeTab === 'audio' && !selectedTrack && (
          <div className={styles.noSelection}>
            <Text size="sm" c="dimmed" ta="center">
              {t('properties.noSelection')}
            </Text>
          </div>
        )}

        {/* Sound Effects content hidden - functionality moved to separate page */}

        {activeTab === 'prompts' && (
          <div className={styles.promptsTab}>
            <PromptsTabContent
              onPromptSelect={handlePromptSelect}
              onPromptCopy={handleCopyPrompt}
              projectId={projectId} // Pass projectId to PromptsTabContent
            />
          </div>
        )}
      </div>

      {/* Prompt Modal Overlay */}
      {isPromptModalOpen && selectedPrompt && (
        <div className={styles.promptModalOverlay} onClick={() => setIsPromptModalOpen(false)}>
          <div className={styles.promptModalContainer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.promptModalHeader}>
              <div className={styles.promptModalTitleContainer}>
                <h3 className={styles.promptModalTitle}>{getPromptDisplayTitle(selectedPrompt)}</h3>
                <Text size="sm" c="dimmed" className={styles.promptModalDate}>
                  Created: {formatDate(selectedPrompt.createdAt)}
                </Text>
              </div>
              <button
                className={styles.promptModalClose}
                onClick={() => setIsPromptModalOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className={styles.promptModalContent}>
              {selectedPrompt.promptData?.generatedPrompt && (
                <div className={styles.promptDisplaySection}>
                  <div className={styles.promptSectionHeader}>
                    <h4 className={styles.promptSectionTitle}>Generated Prompt</h4>
                    <button
                      className={styles.promptCopyButton}
                      onClick={() => handleCopyPrompt(selectedPrompt.promptData!.generatedPrompt, selectedPrompt)}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                      Copy
                    </button>
                  </div>
                  <div className={styles.promptText}>
                    {selectedPrompt.promptData.generatedPrompt}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Audio Tab Content Component
const AudioTabContent: React.FC<{
  selectedTrack: any;
  currentTime: number;
  onVolumeChange: (trackId: string, volume: number) => void;
  onMuteToggle: (trackId: string) => void;
  onDuplicate: (trackId: string) => void;
  onSplit: (trackId: string) => void;
  onDelete: (trackId: string) => void;
  onFadeChange: (trackId: string, property: 'fadeInDuration' | 'fadeOutDuration', value: number) => void;
}> = ({
  selectedTrack,
  currentTime,
  onVolumeChange,
  onMuteToggle,
  onDuplicate,
  onSplit,
  onDelete,
  onFadeChange
}) => {
  const { t } = useTranslation('audioEditor');
  
  return (
    <>
      {/* Header */}
      <div className={styles.tabHeader}>
        <h3 className={styles.tabTitle}>{t('properties.title')}</h3>
        <Text size="sm" c="dimmed">{selectedTrack.name}</Text>
      </div>

      {/* Volume Control */}
      <div className={styles.controlGroup}>
        <Text size="sm" fw={600}>{t('properties.volume')}</Text>
        <div className={styles.volumeControl}>
          <button
            className={`${styles.muteButton} ${selectedTrack.isMuted ? styles.muted : ''}`}
            onClick={() => onMuteToggle(selectedTrack.id)}
          >
            {selectedTrack.isMuted ? '🔇' : '🔊'}
          </button>
          <input
            type="range"
            value={selectedTrack.volume * 100}
            onChange={(e) => onVolumeChange(selectedTrack.id, Number(e.target.value) / 100)}
            min={0}
            max={100}
            disabled={selectedTrack.isMuted}
            style={{
              width: '100%',
              height: '6px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '3px',
              outline: 'none',
              cursor: 'pointer'
            }}
          />
          <Text size="xs" className={styles.volumeValue}>
            {Math.round(selectedTrack.volume * 100)}%
          </Text>
        </div>
      </div>

      {/* Track Actions */}
      <div className={styles.controlGroup}>
        <Text size="sm" fw={600}>{t('actions.duplicate')}</Text>
        <div className={styles.actionButtons}>
          <button
            onClick={() => onDuplicate(selectedTrack.id)}
            className={styles.actionButton}
          >
            📋 {t('actions.duplicate')}
          </button>
          <button
            onClick={() => onSplit(selectedTrack.id)}
            disabled={currentTime <= selectedTrack.startTime || currentTime >= selectedTrack.startTime + selectedTrack.duration}
            className={styles.actionButton}
          >
            ✂️ {t('actions.split')}
          </button>
          <button
            onClick={() => onDelete(selectedTrack.id)}
            className={`${styles.actionButton} ${styles.deleteButton}`}
          >
            🗑️ {t('actions.delete')}
          </button>
        </div>
      </div>

      {/* Fade Controls */}
      <div className={styles.controlGroup}>
        <Text size="sm" fw={600}>{t('properties.fadeEffects')}</Text>
        <div className={styles.fadeControls}>
          <div className={styles.fadeControl}>
            <Text size="xs">{t('properties.fadeIn')}</Text>
            <input
              type="range"
              value={selectedTrack.fadeInDuration}
              onChange={(e) => onFadeChange(selectedTrack.id, 'fadeInDuration', Number(e.target.value))}
              min={0}
              max={selectedTrack.duration / 2}
              step={0.1}
              style={{
                width: '100%',
                height: '4px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
            <Text size="xs">{selectedTrack.fadeInDuration.toFixed(1)}s</Text>
          </div>
          <div className={styles.fadeControl}>
            <Text size="xs">{t('properties.fadeOut')}</Text>
            <input
              type="range"
              value={selectedTrack.fadeOutDuration}
              onChange={(e) => onFadeChange(selectedTrack.id, 'fadeOutDuration', Number(e.target.value))}
              min={0}
              max={selectedTrack.duration / 2}
              step={0.1}
              style={{
                width: '100%',
                height: '4px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
            <Text size="xs">{selectedTrack.fadeOutDuration.toFixed(1)}s</Text>
          </div>
        </div>
      </div>
    </>
  );
};

// Sound Effects Tab Content Component
const SoundEffectsTabContent: React.FC<{
  userId: string;
  onSoundEffectAdd: (soundEffectUrl: string, filename: string, duration: number) => void;
  onOpenSoundEffectsModal: () => void;
  refreshTrigger: number;
}> = ({ userId, onSoundEffectAdd, onOpenSoundEffectsModal, refreshTrigger }) => {
  return (
    <>
      {/* Header */}
      <div className={styles.tabHeader}>
        <h3 className={styles.tabTitle}>Sound Effects</h3>
        <Text size="sm" c="dimmed">Generate and manage sound effects</Text>
      </div>

      {/* Generate Button */}
      <div className={styles.controlGroup}>
        <button
          className={styles.generateButton}
          onClick={onOpenSoundEffectsModal}
        >
          ✨ Generate Sound Effect
        </button>
      </div>

      {/* Sound Effects List */}
      <div className={styles.controlGroup}>
        <Text size="sm" fw={600}>Your Sound Effects</Text>
        <SoundEffectsList
          userId={userId}
          onSoundEffectAdd={onSoundEffectAdd}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </>
  );
};

// Prompts Tab Content Component
const PromptsTabContent: React.FC<{
  onPromptSelect: (prompt: string, project: PromptProject) => void;
  onPromptCopy: (prompt: string, project: PromptProject) => void;
  projectId?: string; // Add projectId prop
}> = ({ onPromptSelect, onPromptCopy, projectId }) => {
  const { t } = useTranslation('audioEditor');
  
  return (
    <>
      {/* Header */}
      <div className={styles.tabHeader}>
        <h3 className={styles.tabTitle}>{t('prompts.title')}</h3>
        <Text size="sm" c="dimmed">
          {projectId ? t('prompts.subtitle') : t('prompts.subtitleAll')}
        </Text>
      </div>

      {/* Prompt Browser */}
      <div className={styles.controlGroup}>
        <PromptBrowser
          onPromptSelect={onPromptSelect}
          onPromptCopy={onPromptCopy}
          height="400px"
          className={styles.editorPromptBrowser}
          projectId={projectId} // Pass projectId to filter prompts
        />
      </div>
    </>
  );
};

// Sound Effects List Component
const SoundEffectsList: React.FC<{
  userId: string;
  onSoundEffectAdd: (soundEffectUrl: string, filename: string, duration: number) => void;
  refreshTrigger: number;
}> = ({ userId, onSoundEffectAdd, refreshTrigger }) => {
  const [soundEffects, setSoundEffects] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Load sound effects on mount and when refreshTrigger changes
  React.useEffect(() => {
    const loadSoundEffects = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // 🔒 UPDATED: Use new authenticated endpoint
        const response = await fetch(getApiUrl('/music-generation/sound-effects'), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setSoundEffects(result.data?.soundEffects || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load sound effects');
        console.error('Error loading sound effects:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadSoundEffects();
  }, [userId, refreshTrigger]);

  const handleSoundEffectEdit = async (filename: string, newPrompt: string) => {
    try {
      console.log('Updating sound effect:', filename, 'New prompt:', newPrompt);
      
      // Call the API to update the sound effect
      const response = await fetch(getApiUrl('/music-generation/sound-effects/update'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          filename: filename,
          prompt: newPrompt
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Sound effect updated successfully:', result);
        
        // Update the local state immediately to reflect the change
        setSoundEffects(prevEffects => 
          prevEffects.map(effect => 
            effect.filename === filename ? { ...effect, prompt: newPrompt } : effect
          )
        );
      } else {
        console.error('Failed to update sound effect:', response.statusText);
      }
    } catch (error) {
      console.error('Error updating sound effect:', error);
    }
  };

  // Load sound effects on mount and when refreshTrigger changes
  React.useEffect(() => {
    const loadSoundEffects = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // 🔒 UPDATED: Use new authenticated endpoint  
        const response = await fetch(getApiUrl('/music-generation/sound-effects'), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setSoundEffects(result.data?.soundEffects || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load sound effects');
        console.error('Error loading sound effects:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      loadSoundEffects();
    }
  }, [userId, refreshTrigger]);

  if (isLoading) {
    return <div className={styles.loading}>Loading sound effects...</div>;
  }

  if (error) {
    return <div className={styles.error}>Error: {error}</div>;
  }

  if (soundEffects.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text size="sm" c="dimmed" ta="center">
          No sound effects yet. Generate your first one!
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.soundEffectsList}>
      {soundEffects.map((effect, index) => (
        <SoundEffectItem
          key={index}
          effect={effect}
          onAdd={() => onSoundEffectAdd(effect.s3Url, effect.filename, effect.duration || 10)}
          onEdit={handleSoundEffectEdit}
          onDelete={() => console.log('Delete sound effect:', effect.filename)}
        />
      ))}
    </div>
  );
};

// Individual Sound Effect Item Component
const SoundEffectItem: React.FC<{
  effect: any;
  onAdd: () => void;
  onEdit: (filename: string, newPrompt: string) => void;
  onDelete: () => void;
}> = ({ effect, onAdd, onEdit, onDelete }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editPrompt, setEditPrompt] = React.useState(effect.prompt || 'Unknown Sound Effect');
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleEditSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (editPrompt.trim() && editPrompt !== effect.prompt) {
      onEdit(effect.filename, editPrompt.trim());
    }
    setIsEditing(false);
  };

  const handleEditCancel = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setEditPrompt(effect.prompt || 'Unknown Sound Effect');
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    // Save on blur instead of cancel
    handleEditSubmit();
  };

  return (
    <div className={styles.soundEffectItem} onClick={isEditing ? undefined : onAdd}>
      <audio
        ref={audioRef}
        src={effect.s3Url}
        onEnded={handleAudioEnded}
        preload="none"
      />
      
      <div className={styles.effectInfo}>
        {isEditing ? (
          <form onSubmit={handleEditSubmit} onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              onKeyDown={handleEditCancel}
              onBlur={handleBlur}
              className={styles.editInput}
              autoFocus
              style={{
                width: '100%',
                padding: '4px 8px',
                fontSize: '14px',
                fontWeight: 500,
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: '#fff'
              }}
            />
          </form>
        ) : (
          <>
            <Text size="sm" fw={500} truncate>
              {effect.prompt || 'Unknown Sound Effect'}
            </Text>
            <Text size="xs" c="dimmed">
              {effect.duration ? `${effect.duration.toFixed(1)}s` : 'Duration unknown'}
            </Text>
          </>
        )}
      </div>

      <div className={styles.effectControls}>
        <button
          className={styles.playButton}
          onClick={(e) => {
            e.stopPropagation();
            togglePlay();
          }}
        >
          {isPlaying ? '⏸️' : '▶️'}
        </button>
        <button
          className={styles.editButton}
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
        >
          ✏️
        </button>
        <button
          className={styles.deleteButton}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          ❌
        </button>
      </div>
    </div>
  );
};

export default TabContainer;